import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { SKIPS, PHONE_DISPLAY, SERVICE_AREA_KEYS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/15";

const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink/60";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  address: "",
  eircode: "",
  skip_size: "4yd",
  delivery_date: "",
  notes: "",
  payment_method: "online",
};

export const BookingForm = ({ selectedSkip }) => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (selectedSkip) setForm((f) => ({ ...f, skip_size: selectedSkip }));
  }, [selectedSkip]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const [eircodeWarn, setEircodeWarn] = useState(false);
  const onEircode = (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, eircode: v }));
    const key = v.replace(/\s/g, "").toUpperCase().slice(0, 3);
    setEircodeWarn(/^[A-Z]\d[\dA-Z]$/.test(key) && !SERVICE_AREA_KEYS.has(key));
  };
  const chosen = SKIPS.find((s) => s.id === form.skip_size);
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, form);
      sessionStorage.setItem(
        "easyskip_pending_booking",
        JSON.stringify({ ...data, email: form.email, phone: form.phone, date: form.delivery_date })
      );
      const pay = await axios.post(`${API}/payments/checkout`, {
        lookup_key: `skip_${form.skip_size}`,
        origin_url: window.location.origin,
        booking_ref: data.reference,
      });
      window.location.href = pay.data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : `Could not submit your booking — please call us on ${PHONE_DISPLAY}.`
      );
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-6 shadow-[0_16px_50px_rgba(0,0,0,0.05)] sm:p-10">
      <AnimatePresence mode="wait">
        {confirmation ? (
          <motion.div
            key="done"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="py-6 text-center"
            data-testid="booking-confirmation"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-700" />
            <h3 className="mt-5 font-heading text-2xl font-extrabold text-ink">
              Booking received
            </h3>
            <p className="mt-2 font-mono text-sm tracking-[0.2em] text-emerald-700" data-testid="booking-reference">
              REF · {confirmation.reference}
            </p>
            <p className="mx-auto mt-5 max-w-sm text-sm leading-relaxed text-ink/65">
              We'll call <span className="font-semibold text-ink">{confirmation.phone}</span>{" "}
              shortly to confirm your delivery slot.
            </p>
            <button
              data-testid="booking-another-button"
              onClick={() => {
                setForm(initialForm);
                setConfirmation(null);
              }}
              className="mt-7 rounded-full border border-ink/20 px-6 py-3 font-heading text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
            >
              Make another booking
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            onSubmit={submit}
            className="space-y-5"
            data-testid="booking-form"
          >
            <div>
              <label className={labelCls} htmlFor="skip-size">Skip size</label>
              <select
                id="skip-size"
                data-testid="booking-form-skip-select"
                required
                value={form.skip_size}
                onChange={set("skip_size")}
                className={`${inputCls} appearance-none`}
              >
                {SKIPS.filter((s) => s.price).map((s) => (
                  <option key={s.id} value={s.id} className="bg-white text-ink">
                    {s.name} ({s.tag}) — {s.priceLabel}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-ink/55">
                Need an 8, 14 or roll-on skip?{" "}
                <a href="/contact?subject=Skip%20enquiry" className="font-semibold text-emerald-700">
                  Send us an enquiry
                </a>
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="bk-name">Full name</label>
                <input
                  id="bk-name"
                  data-testid="booking-form-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Mary Murphy"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-phone">Phone</label>
                <input
                  id="bk-phone"
                  data-testid="booking-form-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="087 123 4567"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="bk-email">Email</label>
              <input
                id="bk-email"
                data-testid="booking-form-email-input"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@example.ie"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="bk-address">Delivery address</label>
              <input
                id="bk-address"
                data-testid="booking-form-address-input"
                required
                value={form.address}
                onChange={set("address")}
                placeholder="12 Chapel Lane, Naas, Co. Kildare"
                className={inputCls}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="bk-eircode">Eircode (optional)</label>
                <input
                  id="bk-eircode"
                  data-testid="booking-form-eircode-input"
                  value={form.eircode}
                  onChange={onEircode}
                  placeholder="W91 AB2C"
                  className={inputCls}
                />
                {eircodeWarn && (
                  <p
                    data-testid="eircode-warning"
                    className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800"
                  >
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    That Eircode looks outside Dublin &amp; Kildare — we only deliver in those
                    counties. Double-check it, or send us an enquiry and we'll see what we can do.
                  </p>
                )}
              </div>
              <div>
                <label className={labelCls} htmlFor="bk-date">Delivery date</label>
                <input
                  id="bk-date"
                  data-testid="booking-form-date-input"
                  required
                  type="date"
                  min={today}
                  value={form.delivery_date}
                  onChange={set("delivery_date")}
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="bk-notes">Anything we should know? (optional)</label>
              <textarea
                id="bk-notes"
                data-testid="booking-form-notes-input"
                rows={3}
                value={form.notes}
                onChange={set("notes")}
                placeholder="Skip goes on the road — permit needed."
                className={`${inputCls} resize-none`}
              />
            </div>

            <button
              type="submit"
              data-testid="booking-form-submit-button"
              disabled={sending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink py-4 font-heading text-base font-bold text-white transition-colors duration-300 hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Taking you to secure checkout…
                </>
              ) : (
                <>
                  Book &amp; Pay {chosen?.priceLabel || "Online"}
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>
            <p className="text-center text-xs text-ink/50">
              Secure card payment via Stripe · 5-day hire · 3-tonne limit · free collection
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
